import React, { useEffect, useState, useRef } from "react";
import JSON5 from "json5";
import { jsonrepair } from "jsonrepair";
import styles from "./styles.scss";
import { v4 as uuidv4 } from "uuid";
import { DashCircle, PlusCircle } from "react-bootstrap-icons";
import { Input, Select, Tooltip, Button } from "antd";
import { InfoCircleFilled } from "@ant-design/icons";

import { InfoCircleDiv } from "../InfoHoverableDiv";

const generateUniqueId = () => {
  return uuidv4();
};

function isValidRegex(str) {
  try {
    new RegExp(str);
    return true;
  } catch (e) {
    if (e instanceof SyntaxError) {
      return false;
    }
    throw e;
  }
}

const dataTypes = ["string", "number", "array", "object", "boolean"];

/**
 * Flattens a row tree down to its leaf rows, skipping object/array containers.
 *
 * @param {Array} rows - The row tree to flatten.
 * @param {Array} acc - Accumulator for leaf options (used in recursion).
 * @returns {Array<{value: string, label: string}>} Flat list of leaf options,
 *          each shaped as `{ value: uniqueIdentifier, label: key }` —
 *          used to populate the multi-select that bulk-sets "check for value"
 *          on a chosen subset of fields.
 */
const collectLeafOptions = (rows, acc = []) => {
  (rows || []).forEach((row) => {
    if (Array.isArray(row.value)) collectLeafOptions(row.value, acc);
    else acc.push({ value: row.uniqueIdentifier, label: row.key || "(empty)" });
  });
  return acc;
};

export const defaultParameterNotation = (props) => {
  const {
    key = "",
    type = "string",
    required = true,
    exatclyMatch = false,
    value = "",
    urlParam = "",
  } = props;

  const isExactlyMatch = dataTypes.indexOf(value) > 0;

  return {
    uniqueIdentifier: generateUniqueId(),
    key: key,
    type: type,
    required: required,
    exatclyMatch: isExactlyMatch,
    value: value,
    urlParam: urlParam,
  };
};

const generateRows = (data, setData, showUrlParam = false) => {
  let TableRows;

  const generateNestedTable = (rawData, depth = 0, recursivelyCalled) => {
    if (typeof rawData === "object" && rawData !== null && !Array.isArray(rawData))
      rawData = [rawData];

    // Guard: a non-iterable can reach here (e.g. a stringified-JSON value that
    // was mistakenly flagged as nested). Never call .map on a non-array.
    if (!Array.isArray(rawData)) return null;

    const results = rawData.map((row, idx) => {
      let isNested = false;
      let value = row.value;
      let key = row.key;
      const uniqueIdentifier = row.uniqueIdentifier;
      let required = row.required;
      let exatclyMatch = row.exatclyMatch;
      let type = row.type;
      let urlParam = row.urlParam;

      const handleChange = (e, uniqueIdentifier, fieldType) => {
        let newValue;

        switch (fieldType) {
          case "key":
            newValue = e.target.value;
            break;
          case "required":
            newValue = e.target.dataset.value === "true" ? true : false;
            break;
          case "match":
            newValue = e.target.dataset.value === "true" ? true : false;
            break;
          case "dataType":
            newValue = e.target.dataset.value;
            break;
          case "value_custom":
            newValue = e.target.value;
            break;
          case "urlParam":
            newValue = e.target.value;
            break;
          default:
        }

        function findObjectById(object, id) {
          if (object.uniqueIdentifier === id) return object;

          for (let key in object) {
            if (typeof object[key] === "object" && object[key] !== null) {
              const foundObject = findObjectById(object[key], id);

              if (foundObject) {
                // Only the target element (matching id) should be modified,
                // not parent objects encountered during recursion
                if (foundObject.uniqueIdentifier === id) {
                  // Only clear nested value when changing dataType away from object/array
                  if (
                    fieldType === "dataType" &&
                    typeof foundObject.value === "object"
                  ) {
                    foundObject.value = "";
                    isNested = false;
                  }

                  switch (fieldType) {
                    case "key":
                      foundObject.key = newValue;
                      break;
                    case "required":
                      foundObject.required = newValue;
                      break;
                    case "match":
                      foundObject.exatclyMatch = newValue;
                      break;
                    case "dataType":
                      foundObject.type = newValue;
                      break;
                    case "value_custom":
                      foundObject.value = newValue;
                      break;
                    case "urlParam":
                      foundObject.urlParam = newValue;
                      break;
                    default:
                  }

                  if (fieldType !== "urlParam" && !foundObject.exatclyMatch)
                    foundObject.value = "";
                }

                return foundObject;
              }
            }
          }

          return null;
        }

        const newObj = findObjectById(data, uniqueIdentifier);

        const updatedData = data.map((elem) => {
          switch (elem.uniqueIdentifier === uniqueIdentifier) {
            case true:
              return newObj;
            default:
              return elem;
          }
        });

        setData(updatedData);
      };

      /**
       * Determines whether a value should expand into a nested sub-table.
       *
       * @remarks
       * Only genuine objects or arrays qualify as nested strings that merely
       * resemble JSON (e.g. Tealium's stringified `ls.*` / `ss.*` blobs) are
       * treated as leaf values. Since these strings are never parsed, recursing
       * into them would call `.map()` on a string and crash.
       */
      if (typeof value === "object" && value !== null) {
        isNested = true;
      } else {
        isNested = false;
      }

      const handleDeleteRow = (e, uniqueIdentifier) => {
        function removeObjectById(array, id) {
          return array.filter((item) => {
            if (item.uniqueIdentifier === id) {
              // If the current item matches the id, exclude it from the new array
              return false;
            } else if (Array.isArray(item.value)) {
              // If the item has a nested array, recursively call this function on the nested array
              item.value = removeObjectById(item.value, id);
            }
            // Include all other items in the new array
            return true;
          });
        }

        data = removeObjectById(data, uniqueIdentifier);
        setData(data);
      };

      const handleAddRow = (e, uniqueIdentifier) => {
        function addObjectCopyById(array, id) {
          return array.flatMap((item) => {
            if (item.uniqueIdentifier === id) {
              // If the current item matches the id, include it in the new array and add a copy of it with default values
              const newObject = defaultParameterNotation({
                key: "",
                type: "string",
                required: true,
                exatclyMatch: false,
                value: "",
              });

              const type = item.type;

              if (["object", "array"].indexOf(type) > -1) {
                switch (Array.isArray(item.value)) {
                  case true:
                    item.value.push(newObject);
                    break;
                  default:
                    item.value = [newObject];
                }

                return [item];
              } else {
                return [item, newObject];
              }
            } else if (Array.isArray(item.value)) {
              // If the item has a nested array, recursively call this function on the nested array
              item.value = addObjectCopyById(item.value, id);
            }

            // Include all other items in the new array
            return item;
          });
        }

        const newUpdatedData = addObjectCopyById(data, uniqueIdentifier);
        setData(newUpdatedData);
      };

      const firstTableRow = idx === 0 && !recursivelyCalled;
      const groupBtnGap = !firstTableRow ? { columnGap: ".3rem" } : {};

      if (isNested) {
        return [
          <div key={`${uniqueIdentifier}`} className="dlgEventTable d-flex">
            {typeof key !== "number" && (
              <div className={`indented-${depth} d-flex dlgEventTable`}>
                <div>
                  <Input
                    name="row_key"
                    type="text"
                    defaultValue={key}
                    onChange={(event) =>
                      handleChange(event, uniqueIdentifier, "key")
                    }
                    style={{ height: "100%" }}
                  />
                </div>

                <div>
                  <Select
                    value={type}
                    onChange={(value) =>
                      handleChange(
                        { target: { dataset: { value: value } } },
                        uniqueIdentifier,
                        "dataType",
                      )
                    }
                    // style={{ width: 120 }}
                  >
                    {dataTypes.map((type) => (
                      <Select.Option key={type} value={type}>
                        {type}
                      </Select.Option>
                    ))}
                  </Select>
                </div>

                <div>
                  <Select
                    value={required ? "required" : "optional"}
                    onChange={(value) =>
                      handleChange(
                        {
                          target: {
                            dataset: {
                              value: value === "required" ? "true" : "false",
                            },
                          },
                        },
                        uniqueIdentifier,
                        "required",
                      )
                    }
                    // style={{ width: 120 }}
                  >
                    <Select.Option value="required">required</Select.Option>
                    <Select.Option value="optional">optional</Select.Option>
                  </Select>
                </div>

                <div
                  className="action-button-icon group-action-btns"
                  style={groupBtnGap}
                >
                  {!firstTableRow && (
                    <div
                      onClick={(e) => {
                        handleDeleteRow(e, uniqueIdentifier);
                      }}
                    >
                      <DashCircle className="color-dark" size={18}></DashCircle>
                    </div>
                  )}
                  <div
                    onClick={(e) => {
                      handleAddRow(e, uniqueIdentifier);
                    }}
                  >
                    <PlusCircle className="color-dark" size={18}></PlusCircle>
                  </div>
                </div>
              </div>
            )}
          </div>,
          <div key={`${uniqueIdentifier}_${depth}`}>
            <div colSpan="2">
              <div>
                <div>{generateNestedTable(value, depth + 1, true)}</div>
              </div>
            </div>
          </div>,
        ];
      }

      return (
        <div
          style={{ width: "70em" }}
          className="d-flex dlgEventTable"
          key={`${uniqueIdentifier}_parent`}
        >
          <div className={`indented-${depth}`}>
            <Input
              name="event_key"
              type="text"
              defaultValue={key}
              placeholder={showUrlParam ? "cookie name" : "event"}
              onChange={(event) => handleChange(event, uniqueIdentifier, "key")}
              //   style={{ height: "100%" }}
            />
          </div>

          {showUrlParam && (
            <div>
              <Input
                name="event_url_param"
                type="text"
                defaultValue={urlParam}
                placeholder="url parameter"
                onChange={(event) =>
                  handleChange(event, uniqueIdentifier, "urlParam")
                }
              />
            </div>
          )}

          <div>
            <Select
              value={type}
              onChange={(value) =>
                handleChange(
                  { target: { dataset: { value: value } } },
                  uniqueIdentifier,
                  "dataType",
                )
              }
              //   style={{ width: 120 }}
            >
              {dataTypes.map((type) => (
                <Select.Option key={type} value={type}>
                  {type}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div>
            <Select
              value={required ? "required" : "optional"}
              onChange={(value) =>
                handleChange(
                  {
                    target: {
                      dataset: {
                        value: value === "required" ? "true" : "false",
                      },
                    },
                  },
                  uniqueIdentifier,
                  "required",
                )
              }
              //   style={{ width: 120 }}
            >
              <Select.Option value="required">required</Select.Option>
              <Select.Option value="optional">optional</Select.Option>
            </Select>
          </div>

          <div>
            <Select
              value={exatclyMatch ? "yes" : "no"}
              onChange={(value) =>
                handleChange(
                  {
                    target: {
                      dataset: { value: value === "yes" ? "true" : "false" },
                    },
                  },
                  uniqueIdentifier,
                  "match",
                )
              }
              //   style={{ width: 120 }}
            >
              <Select.Option value="yes">yes</Select.Option>
              <Select.Option value="no">no</Select.Option>
            </Select>
          </div>

          <div>
            <Input
              name="event_value"
              type="text"
              defaultValue={!exatclyMatch ? "" : value}
              placeholder="value"
              onChange={(event) =>
                handleChange(event, uniqueIdentifier, "value_custom")
              }
              disabled={
                ["object", "array"].includes(type) || (!exatclyMatch && true)
              }
              status={!exatclyMatch ? "" : isValidRegex(value) ? "" : "error"}
              //   style={{ height: "100%" }}
            />
          </div>

          <div
            className="action-button-icon group-action-btns"
            style={groupBtnGap}
          >
            {!firstTableRow && (
              <div
                onClick={(e) => {
                  handleDeleteRow(e, uniqueIdentifier);
                }}
              >
                <DashCircle className="color-dark" size={18}></DashCircle>
              </div>
            )}
            <div
              onClick={(e) => {
                handleAddRow(e, uniqueIdentifier);
              }}
            >
              <PlusCircle className="color-dark" size={18}></PlusCircle>
            </div>
          </div>
        </div>
      );
    });

    return results;
  };

  if (data) TableRows = generateNestedTable(data, 0, false);

  return TableRows;
};

export const transformDataObject = (data) => {
  let transformedData;

  const extractKeyValues = (data) => {
    const result = [];

    const processValue = (key, value) => {
      const newRowObj = {
        uniqueIdentifier: generateUniqueId(),
        key: key,
        // Pasted fields default to required + exact value-match, capturing the
        // sample's real values. Use the "set all" / multi-select controls to
        // flip the volatile/noise fields to no afterwards.
        required: true,
        exatclyMatch: true,
      };

      if (typeof value === "object") {
        if (Array.isArray(value)) {
          newRowObj["type"] = "array";
        } else {
          newRowObj["type"] = "object";
        }
      } else if (["integer", "float", "number"].includes(value)) {
        newRowObj["type"] = "number";
      } else if ([true, false, "true", "false", "boolean"].includes(value)) {
        newRowObj["type"] = "boolean";
      } else {
        newRowObj["type"] = typeof value;
      }

      if ([null, undefined].includes(value)) {
        newRowObj["value"] = `${value}`;
        newRowObj["type"] = "string";
      } else {
        if (
          ["string", "integer", "float", "boolean", "number"].includes(
            typeof value
          )
        ) {
          if (typeof value === "string") {
            if (!value.startsWith("{") || !value.startsWith("[")) {
              newRowObj["value"] = value;
            }
          } else {
            newRowObj["value"] = value;
          }
        } else if (typeof value === "object") {
          newRowObj["value"] = [];

          if (Array.isArray(value)) {
            value.forEach((item) => {
              if (
                ["string", "integer", "float", "boolean", "number"].includes(
                  typeof item
                )
              ) {
                newRowObj["value"] = item;
              } else if (typeof item === "object") {
                if (Array.isArray(value)) {
                  value.forEach((item) => {
                    if (
                      [
                        "string",
                        "integer",
                        "float",
                        "boolean",
                        "number",
                      ].includes(typeof item)
                    ) {
                      newRowObj["value"] = item;
                    } else if (typeof item === "object") {
                      if (Array.isArray(value)) {
                        value.forEach((item) => {
                          if (
                            [
                              "string",
                              "integer",
                              "float",
                              "boolean",
                              "number",
                            ].includes(typeof item)
                          ) {
                            newRowObj["value"] = item;
                          } else if (typeof item === "object") {
                            Object.entries(item).forEach(([key, value]) => {
                              const processValueChildList = processValue(
                                key,
                                value
                              );

                              const keyExists = newRowObj["value"].find(
                                (elem) =>
                                  elem?.key === processValueChildList?.key
                              );

                              if (!keyExists)
                                newRowObj["value"].push(processValueChildList);
                            });
                          }
                        });
                      } else {
                        Object.entries(value).forEach(([key, value]) => {
                          const processValueChildList = processValue(
                            key,
                            value
                          );
                          newRowObj["value"].push(processValueChildList);
                        });
                      }
                    }
                  });
                } else {
                  Object.entries(value).forEach(([key, value]) => {
                    const processValueChildList = processValue(key, value);
                    newRowObj["value"].push(processValueChildList);
                  });
                }
              }
            });
          } else {
            Object.entries(value).forEach(([key, value]) => {
              const processValueChildList = processValue(key, value);
              newRowObj["value"].push(processValueChildList);
            });
          }
        }
      }
      return newRowObj;
    };

    if (data) {
      Object.entries(data).forEach(([key, value]) => {
        const prossedData = processValue(key, value);
        result.push(prossedData);
      });
    }

    return result;
  };

  if (data) {
    const hasUniqueIdentifier = (obj) => {
      // This function checks if the data has been already formatted or not
      if (obj.hasOwnProperty("uniqueIdentifier")) {
        return true;
      }

      for (let key in obj) {
        if (typeof obj[key] === "object" && obj[key] !== null) {
          if (hasUniqueIdentifier(obj[key])) {
            return true;
          }
        }
      }

      return false;
    };

    const dataAlreadyFormatted = hasUniqueIdentifier(data);

    // If data has already been formatted return data otherwise call the
    // extractKeyValues function to modify the data
    if (dataAlreadyFormatted) {
      transformedData = data;
    } else {
      if (Array.isArray(data)) {
        transformedData = extractKeyValues(data[0]);
      } else {
        transformedData = extractKeyValues(data);
      }
    }
  }

  return transformedData;
};

export const NestedObjectTable = (props) => {
  const data = props.data ? props.data : [];

  const { updateTemplateFunction } = props;
  const { setEditedEventTemplateNewVersion } = props;
  const { stepType } = props;

  // For "check cookie value" steps the same builder doubles as a cookie
  // checklist: it gets an extra "URL parameter" field and clearer labels.
  const isCookieCheck = stepType === "check_cookie_value";

  const [parsedData, setParsedData] = useState(null);
  const [updatedData, setUpdatedData] = useState(null);
  const [selectedFieldIds, setSelectedFieldIds] = useState([]);
  const prevDataRef = useRef(null);

  // Hover states for info icons
  const [showParameterInfo, setShowParameterInfo] = useState(false);
  const [showTypeInfo, setShowTypeInfo] = useState(false);
  const [showRequiredInfo, setShowRequiredInfo] = useState(false);
  const [showMatchInfo, setShowMatchInfo] = useState(false);
  const [showValueInfo, setShowValueInfo] = useState(false);

  const infoContentText = {
    parameter: isCookieCheck
      ? "Add the name of the cookie to check."
      : "Add the parameter (object) name.",
    type: "Select the type of value from the provided options. **Please keep in mind that if you intend to create either an object or an array, you must specify either 'array' or 'object' accordingly.",
    required: "Determine whether the field is required or not.",
    match:
      "Determine whether the value should exactly match a specific value or not.",
    value: "This field accepts regular expressions.",
    urlParam:
      "Name of a query parameter on the scenario's landing URL. The cookie value must equal that parameter's value. Leave blank to use a static expected value instead.",
  };

  // Effect to update parsedData when data prop changes
  useEffect(() => {
    if (data === prevDataRef.current) return;
    prevDataRef.current = data;

    let dataObj = null;

    switch (data && data.length > 0) {
      case true:
        const repairedJson = data && data !== "" ? jsonrepair(data) : "";
        const parsed = repairedJson !== "" ? JSON5.parse(repairedJson) : {};
        const transformedDataObj = transformDataObject(parsed);

        dataObj = transformedDataObj;

        break;

      default:
        const newObject = defaultParameterNotation({
          key: "",
          type: "string",
          required: true,
          exatclyMatch: false,
          value: "",
        });

        data.push(newObject);

        dataObj = data;
    }

    updateTemplateFunction(dataObj);
    setEditedEventTemplateNewVersion(dataObj);
    setParsedData(dataObj);
  }, [data]);

  // Effect to update updatedData when parsedData changes
  useEffect(() => {
    if (parsedData) {
      setUpdatedData(parsedData);
      setEditedEventTemplateNewVersion(parsedData);
    }
  }, [parsedData]);

  const updateRawData = (updatedJsonData) => {
    setParsedData(updatedJsonData);
    updateTemplateFunction(updatedJsonData);
    setEditedEventTemplateNewVersion(updatedJsonData);
  };

  /**
   * Bulk-sets a field (`required` / `exatclyMatch`) on every row, including
   * nested children, so a long pasted template can be flipped in one action
   * instead of toggling each row by hand.
   *
   * @param {string} field  The field key to set on every row (e.g. `"required"`).
   * @param {*} value The value to apply to that field across all rows.
   */
  const bulkSetField = (field, value) => {
    const applyToRows = (rows) =>
      (rows || []).map((row) => {
        const next = { ...row, [field]: value };
        if (Array.isArray(next.value)) next.value = applyToRows(next.value);
        return next;
      });
    if (!Array.isArray(updatedData)) return;
    updateRawData(applyToRows(updatedData));
  };

  /**
   * Apply a field (e.g. exatclyMatch) to just the rows whose ids are selected,
   *  so "check for value" can be set on a chosen subset in one action.
  */ 
  const applyFieldToIds = (field, value, ids) => {
    const idSet = ids instanceof Set ? ids : new Set(ids);
    const applyToRows = (rows) =>
      (rows || []).map((row) => {
        const next = { ...row };
        if (idSet.has(next.uniqueIdentifier)) next[field] = value;
        if (Array.isArray(next.value)) next.value = applyToRows(next.value);
        return next;
      });
    if (!Array.isArray(updatedData)) return;
    updateRawData(applyToRows(updatedData));
  };

  const leafCheckOptions = Array.isArray(updatedData)
    ? collectLeafOptions(updatedData)
    : [];

  /**
   * Compact override so the bulk-action buttons don't inherit the global
   * Button theme's large padding/bold weight and overflow the toolbar row.
   */ 
  const toolbarBtnStyle = {
    height: 24,
    padding: "0 10px",
    fontWeight: 400,
    fontSize: 12,
    minWidth: "unset",
  };

  return (
    <div className="mt-3">
      <div className="d-flex heading-row pb-3" style={{ position: "relative" }}>
        <div style={{ position: "relative" }}>
          <h3 style={{ marginBottom: 0 }} className="modal-category">
            {isCookieCheck ? "Cookie name" : "Parameter"}
            <Tooltip title={infoContentText.parameter}>
              <InfoCircleFilled
                className="info-icon"
                style={{ marginLeft: "10px" }}
              />
            </Tooltip>
          </h3>
          <p style={{ marginBottom: 0, fontSize: "12px" }}>
            {isCookieCheck ? "Name of the cookie" : "Parameter name"}
          </p>
        </div>

        {isCookieCheck && (
          <div style={{ position: "relative" }}>
            <h3 style={{ marginBottom: 0 }} className="modal-category">
              URL parameter
              <Tooltip title={infoContentText.urlParam}>
                <InfoCircleFilled
                  className="info-icon"
                  style={{ marginLeft: "10px" }}
                />
              </Tooltip>
            </h3>
            <p style={{ marginBottom: 0, fontSize: "12px" }}>
              Optional — landing-URL
              <br />
              parameter to compare against
            </p>
          </div>
        )}

        <div
        // style={{ left: 45, position: "relative" }}
        >
          <h3 style={{ marginBottom: 0 }} className="modal-category">
            Format
            <Tooltip title={infoContentText.type}>
              <InfoCircleFilled
                className="info-icon"
                style={{ marginLeft: "10px" }}
              />
            </Tooltip>
          </h3>
          <p style={{ marginBottom: 0, fontSize: "12px" }}>Type of parameter</p>
        </div>

        <div
        // style={{ left: 25, position: "relative" }}
        >
          <h3 style={{ marginBottom: 0 }} className="modal-category">
            Required?
            <Tooltip title={infoContentText.required}>
              <InfoCircleFilled
                className="info-icon"
                style={{ marginLeft: "10px" }}
              />
            </Tooltip>
          </h3>
          <p style={{ marginBottom: 0, fontSize: "12px" }}>
            Parameter required?
          </p>
          <Select
            size="small"
            placeholder="set all…"
            value={null}
            onChange={(v) => bulkSetField("required", v === "required")}
            style={{ width: 120, marginTop: 4 }}
          >
            <Select.Option value="required">required</Select.Option>
            <Select.Option value="optional">optional</Select.Option>
          </Select>
        </div>

        <div
        // style={{ left: -8, position: "relative" }}
        >
          <h3 style={{ marginBottom: 0 }} className="modal-category">
            Check for value?
            <Tooltip title={infoContentText.match}>
              <InfoCircleFilled
                className="info-icon"
                style={{ marginLeft: "10px" }}
              />
            </Tooltip>
          </h3>
          <p style={{ marginBottom: 0, fontSize: "12px" }}>
            Should value exactly
            <br />
            match a specific value?
          </p>
          <Select
            size="small"
            placeholder="set all…"
            value={null}
            onChange={(v) => bulkSetField("exatclyMatch", v === "yes")}
            style={{ width: 120, marginTop: 4 }}
          >
            <Select.Option value="yes">yes</Select.Option>
            <Select.Option value="no">no</Select.Option>
          </Select>
        </div>

        <div
        // style={{ left: -53, position: "relative" }}
        >
          <h3 style={{ marginBottom: 0 }} className="modal-category">
            {isCookieCheck ? "Expected value (static)" : "Value"}
            <Tooltip title={infoContentText.value}>
              <InfoCircleFilled
                className="info-icon"
                style={{ marginLeft: "10px" }}
              />
            </Tooltip>
          </h3>
          <p style={{ marginBottom: 0, fontSize: "12px" }}>
            {isCookieCheck
              ? "Used when no URL parameter is set"
              : "If yes, what should the value be?"}
          </p>
        </div>
      </div>

      <div className="page-separator-wide"></div>
      <div
        className="d-flex"
        style={{
          gap: 4,
          alignItems: "center",
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        <span style={{ fontSize: 12 }}>Selected fields:</span>
        <Select
          mode="multiple"
          allowClear
          placeholder="select fields…"
          value={selectedFieldIds}
          onChange={setSelectedFieldIds}
          options={leafCheckOptions}
          maxTagCount="responsive"
          style={{ width: 200 }}
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
        />
        <span style={{ fontSize: 12 }}>Required:</span>
        <Button
          size="small"
          style={toolbarBtnStyle}
          disabled={selectedFieldIds.length === 0}
          onClick={() => applyFieldToIds("required", true, selectedFieldIds)}
        >
          required
        </Button>
        <Button
          size="small"
          style={toolbarBtnStyle}
          disabled={selectedFieldIds.length === 0}
          onClick={() => applyFieldToIds("required", false, selectedFieldIds)}
        >
          optional
        </Button>
        <span style={{ fontSize: 12 }}>Check value:</span>
        <Button
          size="small"
          style={toolbarBtnStyle}
          disabled={selectedFieldIds.length === 0}
          onClick={() =>
            applyFieldToIds("exatclyMatch", true, selectedFieldIds)
          }
        >
          yes
        </Button>
        <Button
          size="small"
          style={toolbarBtnStyle}
          disabled={selectedFieldIds.length === 0}
          onClick={() =>
            applyFieldToIds("exatclyMatch", false, selectedFieldIds)
          }
        >
          no
        </Button>
      </div>
      <div>
        {updatedData &&
          data &&
          generateRows(updatedData, updateRawData, isCookieCheck)}
      </div>
    </div>
  );
};
